import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { IMG } from "@/lib/content";
import { useDriftTheme } from "@/lib/theme";
import { Wordmark } from "@/components/public/Logo";
import { ThemeToggle } from "@/components/drift/DriftLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/* Demo environment: one administrator account, prefilled. */
const ADMIN = { email: "kjetil@k-el.no", password: "demo1234", name: "Kjetil Haugland", title: "Dagleg leiar · administrator", initials: "KH" };

export default function Login() {
  const { theme, toggle } = useDriftTheme();
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(ADMIN.email);
  const [password, setPassword] = useState(ADMIN.password);
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to={user.role === "montor" ? "/drift/montor" : "/drift"} replace />;

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      const dest = location.state?.from || (u.role === "montor" ? "/drift/montor" : "/drift");
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Feil e-post eller passord"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background text-foreground lg:grid-cols-12" data-testid="drift-login-page" data-theme={theme}>
      {/* Photo panel */}
      <div className="relative hidden overflow-hidden bg-ink lg:col-span-7 lg:block">
        <img src={IMG.svc_samferdsel} alt="" className="img-premium absolute inset-0 h-full w-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-ink/30" aria-hidden />
        <div className="relative flex h-full flex-col justify-between p-10 xl:p-14">
          <Link to="/" className="inline-flex items-center gap-2.5 text-white" aria-label="Til nettsida">
            <Wordmark light size="lg" />
          </Link>
          <div>
            <p className="text-[13.5px] font-medium text-white/55">Driftsportal</p>
            <h1 className="t-h1 mt-4 max-w-[16ch] text-white">Oppdrag, folk og dokumentasjon.</h1>
            <p className="mt-5 max-w-md text-[16px] leading-relaxed text-white/70">Frå henvending til fakturagrunnlag. Det interne arbeidsverktøyet til Kvinnherad Elektro.</p>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col lg:col-span-5">
        <div className="flex items-center justify-between px-6 py-5 sm:px-10">
          <Link to="/" className="inline-flex items-center gap-2.5 text-foreground lg:invisible" aria-label="Til nettsida">
            <Wordmark size="md" />
          </Link>
          <ThemeToggle theme={theme} onToggle={toggle} testid="drift-login-theme-toggle" />
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
          <div className="w-full max-w-[400px]">
            <p className="text-[13.5px] font-medium text-muted-foreground">Driftsportal</p>
            <h2 className="mt-2 font-display text-[2.1rem] font-medium leading-[1.05] tracking-[-0.03em]">Logg inn</h2>

            <div className="shadow-panel mt-8 flex items-center gap-3 rounded-xl bg-card px-3.5 py-3" data-testid="drift-login-account">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-foreground font-display text-xs font-semibold text-background">{ADMIN.initials}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-medium leading-tight">{ADMIN.name}</p>
                <p className="truncate text-[12px] text-muted-foreground">{ADMIN.title}</p>
              </div>
              <span className="rounded-md bg-secondary px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-muted-foreground">Demo</span>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-5" data-testid="drift-login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[12.5px] font-medium text-muted-foreground">E-post</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-lg bg-card" autoComplete="username" data-testid="drift-login-email-input" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[12.5px] font-medium text-muted-foreground">Passord</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-lg bg-card" autoComplete="current-password" data-testid="drift-login-password-input" required />
              </div>
              <Button type="submit" disabled={busy} className="h-11 w-full rounded-lg bg-brand text-[15px] font-medium text-brand-foreground hover:bg-brand-dark" data-testid="drift-login-submit-button">
                {busy ? "Loggar inn …" : <>Logg inn <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </form>

            <p className="mt-6 text-[12.5px] leading-relaxed text-muted-foreground">Innloggingsdetaljane er fylte ut for demo-miljøet. Du kan endre dei om du vil logge inn som ein annan brukar.</p>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-5 text-[12.5px] text-muted-foreground sm:px-10">
          <span>Kvinnherad Elektro AS</span>
          <Link to="/" className="u-link inline-flex items-center gap-1 hover:text-foreground" data-testid="drift-login-public-link">Til nettsida <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </div>
      </div>
    </div>
  );
}
